import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { MdLocalShipping, MdPublic, MdHome, MdMap } from 'react-icons/md';

import { fetchDeliverySettings, updateDeliverySettings } from '../../../services/Http/Api';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import MainButton from '../../../shared/layout/buttons/MainButton';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import CountrySelector from './CountrySelector';

const SCOPES = [
    { value: 'domestic', icon: MdHome },
    { value: 'countries', icon: MdMap },
    { value: 'worldwide', icon: MdPublic },
];

/**
 * Where this retailer delivers.
 *
 * Until this existed, a discount only ever reached users in the retailer's own
 * country — so a shop that posts across the border was invisible to everyone on
 * the other side of it. What is set here decides who sees this shop's discounts
 * in the app.
 */
const DeliverySettings = () => {
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [countries, setCountries] = useState([]);
    const [homeCountryId, setHomeCountryId] = useState(null);
    const [scope, setScope] = useState('domestic');
    const [countryIds, setCountryIds] = useState([]);
    const [initial, setInitial] = useState({ scope: 'domestic', countryIds: [] });

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const data = await fetchDeliverySettings();
                if (cancelled) return;
                setCountries(data.countries || []);
                setHomeCountryId(data.home_country_id ?? null);
                setScope(data.scope || 'domestic');
                setCountryIds(data.country_ids || []);
                setInitial({ scope: data.scope || 'domestic', countryIds: data.country_ids || [] });
            } catch (error) {
                if (!cancelled) toast.error(t('Settings.Delivery.loadFailed'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [t]);

    const dirty = useMemo(() => {
        if (scope !== initial.scope) return true;
        if (scope !== 'countries') return false;
        const a = [...countryIds].sort().join(',');
        const b = [...initial.countryIds].sort().join(',');
        return a !== b;
    }, [scope, countryIds, initial]);

    const homeCountry = useMemo(
        () => countries.find((c) => c.id === homeCountryId) || null,
        [countries, homeCountryId],
    );

    const save = async () => {
        if (scope === 'countries' && countryIds.length === 0) {
            toast.error(t('Settings.Delivery.pickAtLeastOne'));
            return;
        }

        setSaving(true);
        try {
            const data = await updateDeliverySettings({ scope, countryIds });
            setScope(data.scope);
            setCountryIds(data.country_ids || []);
            setInitial({ scope: data.scope, countryIds: data.country_ids || [] });
            toast.success(t('Settings.Delivery.saved'));
        } catch (error) {
            toast.error(error?.response?.data?.error || t('Settings.Delivery.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    const page = (content) => (
        <PageContainer>
            <PageHeader>
                <PageTitle icon={<MdLocalShipping size={28} />}>{t('Settings.Delivery.title')}</PageTitle>
            </PageHeader>
            <PageContent>{content}</PageContent>
        </PageContainer>
    );

    if (loading) return page(<LoadingMessage />);

    return page(
        <StyledCard>
            <Intro>{t('Settings.Delivery.subtitle')}</Intro>

            <Scopes>
                {SCOPES.map(({ value, icon: Icon }) => (
                    <ScopeCard key={value} $active={scope === value}>
                        <input
                            type="radio"
                            name="delivery-scope"
                            value={value}
                            checked={scope === value}
                            onChange={() => setScope(value)}
                        />
                        <Icon size={20} />
                        <div>
                            <strong>
                                {value === 'domestic' && homeCountry
                                    ? t('Settings.Delivery.scopes.domesticNamed', { country: homeCountry.name })
                                    : t(`Settings.Delivery.scopes.${value}`)}
                            </strong>
                            <span>{t(`Settings.Delivery.scopeHints.${value}`)}</span>
                        </div>
                    </ScopeCard>
                ))}
            </Scopes>

            {scope === 'countries' && (
                <SelectorBlock>
                    <CountrySelector
                        countries={countries}
                        value={countryIds}
                        onChange={setCountryIds}
                        homeCountryId={homeCountryId}
                        disabled={saving}
                    />
                </SelectorBlock>
            )}

            {/* Said plainly: this is the retailer's own claim, and it decides
                who sees their offers. Nobody should discover that later. */}
            <Note>{t('Settings.Delivery.disclaimer')}</Note>

            <Actions>
                <MainButton type="button" onClick={save} disabled={!dirty || saving}>
                    {saving ? t('Settings.Delivery.saving') : t('Settings.Delivery.save')}
                </MainButton>
            </Actions>
        </StyledCard>
    );
};

export default DeliverySettings;

const Intro = styled.p`
  margin: 0 0 1.25rem;
  font-size: 0.8125rem;
  color: #6b7280;
  max-width: 68ch;
  line-height: 1.55;
`;

const Scopes = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const ScopeCard = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.875rem;
  border: 1px solid ${({ $active }) => ($active ? 'var(--color-primary)' : '#e5e7eb')};
  background: ${({ $active }) => ($active ? 'var(--color-primary-bg-soft)' : '#fff')};
  border-radius: 12px;
  cursor: pointer;
  color: ${({ $active }) => ($active ? 'var(--color-primary)' : '#6b7280')};

  input {
    margin-top: 0.125rem;
    accent-color: var(--color-primary);
  }

  strong {
    display: block;
    font-size: 0.875rem;
    color: #111827;
    margin-bottom: 0.125rem;
  }

  span {
    font-size: 0.75rem;
    color: #6b7280;
    line-height: 1.45;
  }
`;

const SelectorBlock = styled.div`
  margin-bottom: 1.25rem;
`;

const Note = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  background: #f9fafb;
  border-left: 3px solid #e5e7eb;
  padding: 0.625rem 0.75rem;
  border-radius: 0 8px 8px 0;
  margin: 0 0 1.25rem;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;
